/**
 * One session. Spec 11.4: "inspect state and event log; force-end a stuck session."
 *
 * Spec 11.5 again governs the omission: the event log carries no reflection or intention
 * text — spec 6.3 keeps free text out of events deliberately, and the encrypted columns
 * live on other tables — so inspecting a session here cannot reveal what anyone wrote.
 *
 * Force-end is NOT built. `admin.sessions.forceEnd` is in spec 8.2's list, and ending
 * somebody else's live session is a facilitator action taken by a stranger: it needs the
 * same broadcast, the same participant-visible event, and the same refund decision that
 * `session.end` already owns. Wiring the admin path into that is a session-service change
 * rather than a screen, and it is written up rather than half-built.
 */
import type { SessionEventDto } from '@lila/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { Card } from '../../../components/ui/Card';
import { DataTable, type Column } from '../../../components/ui/DataTable';
import { EmptyState } from '../../../components/ui/EmptyState';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { errorKeyFor } from '../../../lib/errors';
import { trpcClient } from '../../../lib/trpc';

export const Route = createFileRoute('/$lang/admin/sessions/$id')({ component: Page });

function Page(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const { id } = Route.useParams();

  const session = useQuery({
    queryKey: ['admin', 'session', id] as const,
    queryFn: () => trpcClient.session.get.query({ sessionId: id }),
  });

  const events = useQuery({
    queryKey: ['admin', 'session', id, 'events'] as const,
    queryFn: () => trpcClient.session.events.query({ sessionId: id, afterSeq: 0, limit: 500 }),
    enabled: session.data !== undefined,
  });

  const columns: readonly Column<SessionEventDto>[] = [
    { id: 'seq', header: 'seq', align: 'end', cell: (row) => row.seq },
    { id: 'type', header: t('admin:audit.action'), cell: (row) => row.type },
    {
      id: 'at',
      header: t('admin:audit.when'),
      hideOnMobile: true,
      cell: (row) => new Date(row.createdAt).toLocaleString(i18n.language),
    },
    {
      id: 'payload',
      header: t('admin:sessions.state'),
      hideOnMobile: true,
      /* Rules payloads only: spec 6.3 keeps free text out of the event log by design. */
      cell: (row) => <code className="text-xs text-ink-muted">{JSON.stringify(row.payload)}</code>,
    },
  ];

  if (session.error !== null) return <Alert tone="error">{t(errorKeyFor(session.error))}</Alert>;
  if (session.isLoading || session.data === undefined) {
    return <SkeletonList rows={4} label={t('admin:nav.sessions')} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">
            {session.data.title ?? session.data.code ?? id.slice(0, 8)}
          </h1>
          <p className="text-sm text-ink-muted">
            {session.data.mode} · {session.data.participants.length}
          </p>
        </div>
        <Badge tone={session.data.status === 'active' ? 'positive' : 'neutral'}>
          {session.data.status}
        </Badge>
      </header>

      <Card title={t('admin:sessions.eventLog')}>
        {events.isLoading ? (
          <SkeletonList rows={6} label={t('admin:sessions.eventLog')} />
        ) : (
          <DataTable
            caption={t('admin:sessions.eventLog')}
            columns={columns}
            rows={events.data ?? []}
            rowKey={(row) => String(row.seq)}
            empty={<EmptyState title={t('admin:sessions.empty')} />}
          />
        )}
      </Card>
    </div>
  );
}
